import admin_devices_model from 'models/admin_devices_model';
import { Form } from 'semantic-ui-react';
import { toastMessage, ToastType } from '@iscr/xAppLib/helpers/toast_message';
import { getTRPCClient } from 'xAppLib/providers/trpcClient';
import { useTRPC } from '@iscr/xAppLib/context/QueryProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import user_model from "models/user_model";
import { useUser } from "xAppLib/Hooks";

const DeviceOrgSelector = ({ upward = undefined }) => {
    const [loading, setLoading] = useState(false);

    //  Fetch the set of orgs this device may be pointed at. Cached until explicitly invalidated, rather than on a
	//  timer, because the call costs several org-tree reads and this component sits in the footer of most pages.
    useUser();
    const trpc = useTRPC();
    const { data: availableOrgs = [] } = useQuery(
        trpc.devices.reachableOrgs.queryOptions(undefined, {
            staleTime: Infinity,
            enabled: user_model.check_access('set_own_device_org'),
        }),
    );

    //  Invalidate and re-fetch availableOrgs if the device is re-registered.
    const queryClient = useQueryClient();
    useEffect(() => {
        const handler = (dvc, lastOrg) => {
            //  Undefined only on the session's first DVC_LDD, where there is no cached list to go stale.
            if (lastOrg === undefined) return;

            //  Both are `false` when unset, and lastOrg can be an empty object before the org has finished loading.
            const prevOid = (lastOrg && lastOrg.oid) || null;
            const nextOid = (dvc?.org && dvc.org.oid) || null;

            if (prevOid !== nextOid) {
                void queryClient.invalidateQueries({ queryKey: trpc.devices.reachableOrgs.queryKey() });
            }
        };

        app.on(app.events.DVC_LDD, handler);

        return () => app.off(app.events.DVC_LDD, handler);
    }, [queryClient, trpc]);

    const availableOrgMap = new Map(availableOrgs.map(o => [o.oid, o]));
    const canSwapDeviceOrg = availableOrgs.length;

	const onDeviceUpdate = async (_, e) => {
        const org = availableOrgMap.get(e.value);

        if (org.oid === app.dvc.org.oid) return null;

        //  Ask the backend for the list of orgs that can still be reached after registering the device to org.oid,
        //  and diff that with the current orgs to display what become unavailable after switching.
        setLoading(true);
        let unavailableOrgsAfterSwitch = [];
        try {
            const after = await getTRPCClient().devices.reachableOrgs.query({ asIfRegisteredTo: org.oid });
            const keeps = new Set(after.map(o => o.oid));
            unavailableOrgsAfterSwitch = availableOrgs.filter(o => !keeps.has(o.oid));
        } finally {
            setLoading(false);
        }
        const showWarning = unavailableOrgsAfterSwitch.length > 0;

        const answer = await app.confirm(`Set Organisation (${org.onum}) ${org.name}`, <div>
            <div>On the current device</div>
            {showWarning && 
                <div className="mt-4">
                    <span className="text-red-800 font-bold">WARNING</span>{" "}
                    By switching to {org.name}, this device will lose access to the following clinics because they're not associated with your profile. Please contact your manager or support for assistance.
                    <ul className="list-disc ml-6 mt-2 font-bold">
                        {unavailableOrgsAfterSwitch.map(o => (
                            <li key={o.oid}>{o.name}</li>
                        ))}
                    </ul>
                </div>
            }
        </div>);

        if (answer === 'yes') {
            setLoading(true);
            try {
                await admin_devices_model.set_dvc_oid({ oid: org.oid });
                // Success is announced by DeviceRegistrationChangeToast, off the device watch.
            } catch (e) {
                toastMessage(
                    <div>
                        <div>Unable to switch to:</div>
                        <div className="font-bold">{org.name}</div>
                        <div>{e?.message || 'Please contact your manager or support for assistance.'}</div>
                    </div>,
                    ToastType.ERROR,
                );
            } finally {
                setLoading(false);
            }
        }
	}

    if (app.dvc?.org && !canSwapDeviceOrg)  return (
        <b>{app.dvc.org.name}<br/></b>
    )

    if (canSwapDeviceOrg) {
        const orgOptions = availableOrgs.map(org => ({ 
            key: org.oid,
            text: org.name,
            value: org.oid,
        })).filter((org) => org.key !== app.dvc.org.oid);
        
        return (
            <Form>
                <Form.Dropdown
                    upward={upward}
                    options={[
                        {
                            key: app.dvc.org.oid,
                            text: <b>{app.dvc.org.name}</b>,
                            value: app.dvc.org.oid
                        },
                        ...orgOptions
                    ]}
                    text={<b>{app.dvc.org.name}</b>}
                    value={app.dvc.org.name}
                    data-testid="dropdown-clinic"
                    onChange={onDeviceUpdate}
                    loading={loading}
                />
            </Form>
        );
    }

    return null;
}

export default DeviceOrgSelector;
